import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Chip,
  Dialog,
  ImageList,
  ImageListItem,
  Skeleton,
  Alert,
  Paper,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  useLostItem,
  useDeleteLostItem,
  useCreateInquiry,
} from "../hooks/useApi";
import { useNotification } from "../contexts/NotificationContext";

const LostItemDetailNew: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: item, isLoading, error } = useLostItem(id || "");
  const deleteItemMutation = useDeleteLostItem();
  const createInquiryMutation = useCreateInquiry();

  const handleCreateInquiry = async () => {
    if (!id) return;

    try {
      const result = await createInquiryMutation.mutateAsync({
        lost_item_id: id,
      });

      // HTTPステータスコードが200の場合は既存のチャット、201の場合は新規作成
      // ただし、mutateAsyncはレスポンスボディのみを返すため、
      // バックエンドが既存のチャットを返した場合でも同じように処理される
      // そのため、メッセージは統一的に「問い合わせページに移動します」とする
      showSuccess("問い合わせページに移動します");
      navigate(`/inquiries/${result.id}`);
    } catch (error: any) {
      showError(`問い合わせの作成に失敗しました: ${error.message}`);
    }
  };

  const canEdit = useMemo(
    () =>
      userProfile &&
      (userProfile.roles.includes("staff") ||
        userProfile.roles.includes("admin")),
    [userProfile]
  );
  const canDelete = useMemo(
    () => userProfile && userProfile.roles.includes("admin"),
    [userProfile]
  );
  // AI解析機能を利用できるか（staff または admin に許可）
  const canAiAnalysis = useMemo(
    () =>
      userProfile &&
      (userProfile.roles.includes("staff") ||
        userProfile.roles.includes("admin")),
    [userProfile]
  );

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setImageDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await deleteItemMutation.mutateAsync(id);
      navigate("/items");
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `落とし物: ${item?.name}`,
          text: `${item?.description}`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Share error:", error);
      }
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      showSuccess("URLをクリップボードにコピーしました");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Found":
        return "success";
      case "Claimed":
        return "warning";
      case "Returned":
        return "primary";
      case "Disposed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "Found":
        return "未引き取り";
      case "Claimed":
        return "引き取り済み";
      case "Returned":
        return "返却完了";
      case "Disposed":
        return "処分済み";
      default:
        return status;
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Alert severity="warning">
          落とし物の詳細を表示するにはログインが必要です。
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ mt: 2 }}
          />
          <Skeleton variant="text" width="100%" height={200} sx={{ mt: 2 }} />
        </Box>
      </Container>
    );
  }

  if (error || !item) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          {error?.message || "落とし物が見つかりませんでした。"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            落とし物詳細
          </Typography>
          <Typography variant="body1" color="text.secondary">
            落とし物の詳細情報と画像を確認できます。
          </Typography>
        </Box>

        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="/items"
            onClick={(e) => {
              e.preventDefault();
              navigate("/items");
            }}
            sx={{ cursor: "pointer" }}
          >
            落とし物一覧
          </Link>
          <Typography color="text.primary">{item.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {item.name}
                </Typography>
                <Chip
                  label={getStatusText(item.status)}
                  color={getStatusColor(item.status) as any}
                  size="medium"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="共有">
                  <IconButton onClick={handleShare}>
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="印刷">
                  <IconButton onClick={() => window.print()}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<QuestionAnswerIcon />}
                  onClick={handleCreateInquiry}
                  disabled={createInquiryMutation.isPending}
                >
                  問い合わせする
                </Button>
                {canAiAnalysis && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={async () => {
                      if (!currentUser) {
                        showError("認証情報がありません");
                        return;
                      }

                      try {
                        // Firebase User の getIdToken() を利用して Bearer トークンを取得
                        const token = await currentUser.getIdToken();
                        const headers: Record<string, string> = {
                          "Content-Type": "application/json",
                        };
                        if (token) {
                          headers["Authorization"] = `Bearer ${token}`;
                        }

                        const response = await fetch(
                          `${
                            process.env.REACT_APP_API_BASE_URL ||
                            "http://localhost:8080"
                          }/admin/items/${id}/reanalyze-ai`,
                          {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ priority: 5 }),
                          }
                        );

                        if (response.ok) {
                          let result;
                          try {
                            result = await response.json();
                          } catch (jsonError) {
                            console.error("JSON parse error:", jsonError);
                            showSuccess("AI再解析を開始しました");
                            window.location.reload();
                            return;
                          }
                          showSuccess(
                            result.message || "AI再解析を開始しました"
                          );
                          // ページをリロードして最新の状態を表示
                          window.location.reload();
                        } else {
                          let errorMessage = "再解析に失敗しました";
                          try {
                            const error = await response.json();
                            errorMessage = error.message || errorMessage;
                          } catch (jsonError) {
                            console.error("Error JSON parse error:", jsonError);
                            // レスポンステキストを取得してエラーメッセージとする
                            try {
                              const errorText = await response.text();
                              if (errorText) {
                                errorMessage = errorText;
                              }
                            } catch (textError) {
                              console.error(
                                "Error text read error:",
                                textError
                              );
                            }
                          }
                          showError(errorMessage);
                        }
                      } catch (error) {
                        console.error("Reanalyze error:", error);
                        showError("再解析中にエラーが発生しました");
                      }
                    }}
                  >
                    AI再解析
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/items/${id}/edit`)}
                  >
                    編集
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    削除
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/items")}
                >
                  戻る
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
          {/* Images */}
          <Box flex={1}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  画像 ({item.media_urls?.length || 0}枚)
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {item.media_urls && item.media_urls.length > 0 ? (
                  <ImageList cols={1} rowHeight={300} gap={16}>
                    {item.media_urls.map((url, index) => (
                      <ImageListItem
                        key={index}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          overflow: "hidden",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => handleImageClick(index)}
                      >
                        <img
                          src={url}
                          alt={`${item.name} - 画像 ${index + 1}`}
                          loading="lazy"
                          style={{
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <Paper
                    sx={{
                      p: 8,
                      textAlign: "center",
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      画像がありません
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Details */}
          <Box flex={1}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  詳細情報
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <LocationIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        発見場所
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {item.found_location}
                    </Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ScheduleIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        発見日時
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {new Date(item.found_at).toLocaleString("ja-JP", {
                        timeZone: "Asia/Tokyo",
                      })}
                    </Typography>
                  </Box>

                  {item.description && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        説明
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  )}

                  {item.notes && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        備考
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {item.notes}
                      </Typography>
                    </Box>
                  )}

                  {item.claimed_by_firebase_uid && (
                    <Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          受け取り者
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {item.claimed_by_firebase_uid}
                      </Typography>
                    </Box>
                  )}

                  {item.returned_at && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        返却日時
                      </Typography>
                      <Typography variant="body1">
                        {new Date(item.returned_at).toLocaleString("ja-JP", {
                          timeZone: "Asia/Tokyo",
                        })}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <StoreIcon sx={{ mr: 1, color: "text.secondary" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        店舗
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {item.store?.name || "店舗情報なし"}
                    </Typography>
                    {!item.store && item.store_id && (
                      <Typography variant="caption" color="text.secondary">
                        店舗ID: {item.store_id}
                      </Typography>
                    )}
                    {item.store?.contact_email && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <EmailIcon
                          sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          component="a"
                          href={`mailto:${item.store.contact_email}`}
                          sx={{ color: "primary.main", textDecoration: "none" }}
                        >
                          {item.store.contact_email}
                        </Typography>
                      </Box>
                    )}
                    {item.store?.contact_phone && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <PhoneIcon
                          sx={{ mr: 1, fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          component="a"
                          href={`tel:${item.store.contact_phone}`}
                          sx={{ color: "primary.main", textDecoration: "none" }}
                        >
                          {item.store.contact_phone}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* AI Analysis Results */}
            {item.ai_analysis_results && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <AutoAwesomeIcon color="secondary" />
                    <Typography variant="h6">AI解析結果</Typography>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={2}>
                    {item.ai_analysis_results.analysis?.suggested_name && (
                      <TextField
                        label="提案された名前"
                        value={item.ai_analysis_results.analysis.suggested_name}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    {item.ai_analysis_results.analysis
                      ?.suggested_description && (
                      <TextField
                        label="提案された説明"
                        value={
                          item.ai_analysis_results.analysis
                            .suggested_description
                        }
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    )}
                    {item.ai_analysis_results.analysis?.suggested_category && (
                      <TextField
                        label="提案されたカテゴリ"
                        value={
                          item.ai_analysis_results.analysis.suggested_category
                        }
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    {item.ai_analysis_results.analysis?.suggested_tags &&
                      item.ai_analysis_results.analysis.suggested_tags.length >
                        0 && (
                        <TextField
                          label="提案されたタグ"
                          value={item.ai_analysis_results.analysis.suggested_tags.join(
                            ", "
                          )}
                          InputProps={{ readOnly: true }}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    {item.ai_analysis_results.analysis?.raw_text && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          AI解析詳細
                        </Typography>
                        <Card
                          variant="outlined"
                          sx={{ backgroundColor: theme.palette.action.hover }}
                        >
                          <CardContent>
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {item.ai_analysis_results.analysis.raw_text}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Box>
        </Stack>
      </Stack>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
            onClick={() => setImageDialogOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          {item.media_urls && item.media_urls[selectedImageIndex] && (
            <img
              src={item.media_urls[selectedImageIndex]}
              alt={`${item.name} - 画像 ${selectedImageIndex + 1}`}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            削除の確認
          </Typography>
          <Typography gutterBottom>
            「{item.name}」を本当に削除しますか？
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            この操作は取り消せません。
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}
          >
            <Button onClick={() => setDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteItemMutation.isPending}
            >
              削除
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default LostItemDetailNew;
